import { buildQuoteSharePayload } from '../lib/quotePdf';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';
import { Plus, Eye, Upload, MagnifyingGlass, Trash, CheckSquare, Square, Lock } from '@phosphor-icons/react';
import { ViewToggle, useViewMode } from '../components/ViewToggle';
import { useNavigate } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import BulkUploadModal from '../components/BulkUploadModal';
import useAuthStore from '../store/authStore';
import useBulkSelection from '../hooks/useBulkSelection';
import BulkDeleteDialog, { BulkActionBar } from '../components/BulkActions';
import ShareMenu from '../components/ShareMenu';
import OwnerFilter from '../components/OwnerFilter';
import PipelineToolbar from '../components/PipelineToolbar';
import { computeCloseWindow, withinCloseWindow, sortRows, isForecastOverdue } from '../lib/pipelineFilters';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useViewMode('quotations', 'list');
  const [closeWindow, setCloseWindow] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');
  const [forecastOverdueOnly, setForecastOverdueOnly] = useState(false);
  // Status filter — chip row. Values map to `quotation.status`:
  //   'all', 'draft', 'pending' (pending_approval), 'approved', 'rejected'.
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const canBulkUpload = ['Vice President', 'President'].includes(user?.role);
  const canBulkDelete = ['Vice President', 'President'].includes(user?.role);
  // Feb 2026 (iter84) — VP+ / President see a Gross Margin column (backend
  // only enriches gm_amount/gm_pct on /api/quotations for these roles).
  const canSeeGm = ['Vice President', 'President'].includes(user?.role);
  // iter92 — Tender Executive views quotes but cannot create them outside
  // the tender flow.
  const canCreate = user?.role !== 'Tender Executive';
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerFilter]);

  const fetchQuotations = async () => {
    try {
      const params = ownerFilter.length ? `?owner_ids=${ownerFilter.join(',')}` : '';
      const response = await axios.get(`${API}/quotations${params}`);
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuotations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = quotations;
    if (q) {
      list = list.filter(quotation => {
        const hay = [
          quotation.id, quotation.lead_name, quotation.created_by_name,
          quotation.status, quotation.quotation_number,
          String(quotation.total || ''),
        ].filter(Boolean).join(' | ').toLowerCase();
        return hay.includes(q);
      });
    }
    // Close-window filter uses parent lead's expected_closing_date (hydrated
    // server-side into each quote — see /api/quotations enrichment).
    // Overdue = lead close date has passed AND lead is still open.
    if (closeWindow === 'overdue') {
      list = list.filter(q_ => {
        // Build a lead-shaped row so isForecastOverdue's status check works.
        return isForecastOverdue({
          status: q_.lead_status,
          expected_closing_date: q_.expected_closing_date,
        });
      });
    } else if (closeWindow && closeWindow !== 'all') {
      const win = computeCloseWindow(closeWindow);
      list = list.filter(q_ => withinCloseWindow(q_, 'expected_closing_date', win));
    }
    if (forecastOverdueOnly) {
      list = list.filter(q_ => isForecastOverdue({
        status: q_.lead_status,
        expected_closing_date: q_.expected_closing_date,
      }));
    }
    // Status chip filter — direct on `quotation.status`.
    if (statusFilter !== 'all') {
      const map = { pending: 'pending_approval' };
      const target = map[statusFilter] || statusFilter;
      list = list.filter(q_ => q_.status === target);
    }
    // Sort — value key is `total` (quote total), close key stays close date.
    list = sortRows(list, sortBy, { valueKey: 'total', dateKey: 'expected_closing_date' });
    return list;
  }, [quotations, searchTerm, closeWindow, sortBy, forecastOverdueOnly, statusFilter]);

  const visibleIds = useMemo(() => filteredQuotations.map(q => q.id), [filteredQuotations]);
  const selection = useBulkSelection(visibleIds);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'pending_approval': return 'status-pending';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const buildSharePayload = (quotation) => buildQuoteSharePayload(quotation);

  const submitBulkDelete = async ({ reason }) => {
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/quotations/bulk-delete`, {
        quotation_ids: selection.selectedIds,
        reason,
      });
      notify(`✅ ${r.data.deleted} quotation(s) deleted.`);
      setShowBulkDeleteDialog(false);
      selection.clear();
      await fetchQuotations();
    } catch (e) {
      notify(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="quotations-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="quotations-title">
            Quotations
          </h1>
          <p className="text-[#6D8E87]">Manage and track quotations</p>
        </div>
        <div className="flex gap-3">
          {canBulkUpload && (
            <button onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 px-5 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition"
              data-testid="bulk-quotations-button">
              <Upload size={20} weight="bold" /> Bulk Upload
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => navigate(tp('/quotations/create'))}
              className="btn-primary flex items-center gap-2"
              data-testid="create-quotation-button"
            >
              <Plus size={20} weight="bold" />
              Create Quotation
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#6D8E87]" />
          <input
            type="text"
            placeholder="Search by lead, quotation ID, creator, status or total…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none"
            data-testid="quotation-search-input"
          />
        </div>
        <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} testid="quotations-owner-filter" />
      </div>

      <PipelineToolbar
        pageKey="quotations"
        window={closeWindow}
        onWindowChange={setCloseWindow}
        sortBy={sortBy}
        onSortChange={setSortBy}
        forecastOverdueOnly={forecastOverdueOnly}
        onForecastOverdueChange={setForecastOverdueOnly}
      />

      {/* Status filter chip row — Draft / Pending / Approved / Rejected. */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="quotations-status-chips">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] mr-1">Status:</span>
        {[
          { key: 'all',      label: 'All' },
          { key: 'draft',    label: 'Draft' },
          { key: 'pending',  label: 'Pending Approval' },
          { key: 'approved', label: 'Approved' },
          { key: 'rejected', label: 'Rejected' },
        ].map(c => (
          <button
            key={c.key}
            onClick={() => setStatusFilter(c.key)}
            data-testid={`quotations-status-${c.key}`}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
              statusFilter === c.key
                ? 'bg-[#00a69c] text-white border-[#00a69c]'
                : 'bg-white text-[#3B5A54] border-[#D5E8E4] hover:border-[#00a69c]'
            }`}
          >
            {c.label}
          </button>
        ))}
        {statusFilter !== 'all' && (
          <span className="text-[11px] text-[#6D8E87] ml-1">
            · showing {filteredQuotations.length} of {quotations.length}
          </span>
        )}
      </div>

      <div className="flex justify-end mb-3">
        <ViewToggle mode={viewMode} onChange={setViewMode} testIdPrefix="quotations-view" />
      </div>

      {viewMode === 'list' && (
      <div className="medical-card overflow-x-auto pb-24">
        <table className="w-full min-w-[800px]" data-testid="quotations-table">
          <thead className="bg-[#F4F9F7]">
            <tr>
              {canBulkDelete && (
                <th className="text-left px-4 py-3 w-8">
                  <button
                    type="button"
                    onClick={selection.toggleAll}
                    data-testid="quotations-bulk-select-all-toggle"
                    aria-label="Select all quotations"
                  >
                    {selection.isAllSelected
                      ? <CheckSquare size={20} weight="fill" className="text-red-600" />
                      : <Square size={20} className={selection.isSomeSelected ? 'text-red-600' : 'text-[#6D8E87]'} />}
                  </button>
                </th>
              )}
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">ID</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Lead</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Total</th>
              {canSeeGm && (
                <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]" data-testid="quotations-th-gm">GM %</th>
              )}
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Status</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Created By</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuotations.map((quotation) => (
              <tr key={quotation.id} className={`border-t border-[#D5E8E4] hover:bg-[#F4F9F7] ${selection.isSelected(quotation.id) ? 'bg-red-50' : ''}`} data-testid={`quotation-row-${quotation.id}`}>
                {canBulkDelete && (
                  <td className="px-4 py-4 w-8">
                    <button
                      type="button"
                      onClick={() => selection.toggle(quotation.id)}
                      data-testid={`quotation-bulk-checkbox-${quotation.id}`}
                      aria-label="Select quotation"
                    >
                      {selection.isSelected(quotation.id)
                        ? <CheckSquare size={18} weight="fill" className="text-red-600" />
                        : <Square size={18} className="text-[#6D8E87]" />}
                    </button>
                  </td>
                )}
                <td className="px-6 py-4 text-sm text-[#3B5A54]">{quotation.id.slice(0, 8)}</td>
                <td className="px-6 py-4 text-sm font-medium text-[#052A23]">{quotation.lead_name}</td>
                <td className="px-6 py-4 text-sm font-semibold text-[#00a69c]">₹{quotation.total.toLocaleString('en-IN')}</td>
                {canSeeGm && (
                  <td className="px-6 py-4 text-sm" data-testid={`quotation-gm-${quotation.id}`}>
                    {quotation.gm_pct == null ? (
                      <span className="text-[#6D8E87]" title="COGS not captured on line items">—</span>
                    ) : (
                      <span
                        className={`font-semibold ${quotation.gm_pct >= 30 ? 'text-emerald-700' : quotation.gm_pct >= 15 ? 'text-amber-700' : 'text-rose-700'}`}
                        title={`GM ₹${Number(quotation.gm_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}${quotation.gm_complete === false ? ' · partial (some items missing COGS)' : ''}`}
                      >
                        {quotation.gm_pct.toFixed(1)}%
                        {quotation.gm_complete === false && <sup className="text-[9px] text-amber-600">*</sup>}
                      </span>
                    )}
                  </td>
                )}
                <td className="px-6 py-4">
                  <span className={`status-badge ${getStatusColor(quotation.status)}`}>
                    {quotation.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-[#3B5A54]">{quotation.created_by_name}</td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/quotations/${quotation.id}`)}
                      className="w-8 h-8 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition"
                      data-testid={`view-quotation-${quotation.id}`}
                    >
                      <Eye size={18} />
                    </button>
                    {quotation.status === 'approved' ? (
                      <ShareMenu
                        variant="icon"
                        testid={`share-quotation-${quotation.id}`}
                        getPayload={() => buildSharePayload(quotation)}
                      />
                    ) : (
                      <button
                        disabled
                        title={
                          quotation.status === 'pending_approval'
                            ? `Locked — awaiting approval by ${quotation.current_approval_stage || 'senior'}`
                            : quotation.status === 'draft'
                              ? 'Locked — submit for approval to unlock sharing'
                              : 'Locked — quote must be approved before sharing'
                        }
                        className="w-8 h-8 flex items-center justify-center text-gray-300 rounded-lg cursor-not-allowed"
                        data-testid={`share-quotation-${quotation.id}-locked`}
                      >
                        <Lock size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredQuotations.length === 0 && (
              <tr>
                <td colSpan={canBulkDelete ? 7 : 6}>
                  <EmptyState title="No quotations match" hint="Try a different search or clear the filters." testid="quotations-empty" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-24" data-testid="quotations-grid">
          {filteredQuotations.length === 0 ? (
            <div className="col-span-full medical-card"><EmptyState title="No quotations to show" hint="Quotes you create from a lead appear here." testid="quotations-empty-cards" /></div>
          ) : filteredQuotations.map((q) => (
            <div
              key={q.id}
              className={`medical-card p-5 hover:shadow-lg transition cursor-pointer ${selection.isSelected(q.id) ? 'ring-2 ring-red-400' : ''}`}
              onClick={() => navigate(`/quotations/${q.id}`)}
              data-testid={`quotation-card-${q.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-[10px] font-mono text-[#6D8E87]">{q.id.slice(0, 8)}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  q.status === 'approved' ? 'bg-emerald-100 text-emerald-700'
                  : q.status === 'rejected' ? 'bg-rose-100 text-rose-700'
                  : q.status === 'draft' ? 'bg-slate-100 text-slate-600'
                  : 'bg-amber-100 text-amber-700'
                }`}>{(q.status || '').replace(/_/g, ' ').toUpperCase()}</span>
              </div>
              <h3 className="font-semibold text-[#052A23] text-base leading-tight mb-1" style={{ fontFamily: 'Outfit' }}>{q.lead_name || 'Untitled Lead'}</h3>
              <p className="text-2xl font-bold text-[#00a69c] mt-2" style={{ fontFamily: 'Outfit' }}>₹{Number(q.total || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-[#6D8E87] mt-2">Created by <span className="font-medium text-[#3B5A54]">{q.created_by_name || '—'}</span></p>
              {q.created_at && <p className="text-[10px] text-[#6D8E87] mt-1">{new Date(q.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>}
            </div>
          ))}
        </div>
      )}

      <BulkUploadModal open={showBulkModal} onClose={() => setShowBulkModal(false)}
        title="Bulk Upload Quotations" exportUrl="/quotations-export/csv" importUrl="/quotations-import/bulk"
        templateName="quotations_template.csv" onSuccess={fetchQuotations}
        instructions="CSV with columns: lead_name, product_code, quantity, price, discount_percent, transportation, payment_terms, warranty_years, installation, delivery_days. Group multiple items under the same lead_name. T&C fields only needed on the first row per lead." />

      <BulkActionBar count={selection.count} onClear={selection.clear} testid="quotations-bulk-action-bar">
        {canBulkDelete && (
          <button
            type="button"
            onClick={() => setShowBulkDeleteDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 rounded-full transition"
            data-testid="quotations-bulk-delete-btn"
          >
            <Trash size={14} weight="bold" /> Delete
          </button>
        )}
      </BulkActionBar>
      <BulkDeleteDialog
        open={showBulkDeleteDialog}
        onClose={() => setShowBulkDeleteDialog(false)}
        onConfirm={submitBulkDelete}
        entityLabel="quotation" entityLabelPlural="quotations"
        count={selection.count}
        loading={bulkDeleting}
      />
    </div>
  );
};

export default Quotations;
